import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import { sessionsV2Api } from '../../../api/sessionsV2Api';
import BottomSheet from '../../../shared/ui/forms/BottomSheet';
import StatusActionButton from '../../../shared/ui/buttons/StatusActionButton';
import UserList, { User } from '../../../shared/ui/lists/UserList';
import config from '../../../config/config';
import './BulkOpenSessionsSheet.css';

interface BulkOpenSessionsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: number;
  onConfirm: (siteExecutorIds: number[]) => Promise<void>;
  loading?: boolean;
  /** Поверх fullscreen-карточки */
  elevated?: boolean;
}

const BulkOpenSessionsSheet: React.FC<BulkOpenSessionsSheetProps> = ({
  isOpen,
  onClose,
  siteId,
  onConfirm,
  loading = false,
  elevated = false,
}) => {
  const [executors, setExecutors] = useState<SiteExecutor[]>([]);
  const [selectedExecutorIds, setSelectedExecutorIds] = useState<number[]>([]);
  const [executorsLoading, setExecutorsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadExecutors();
      setSelectedExecutorIds([]);
    }
  }, [isOpen, siteId]);

  const loadExecutors = async () => {
    try {
      setExecutorsLoading(true);
      setError(null);
      const [executorsData, activeSessions] = await Promise.all([
        siteExecutorsApi.getSiteExecutors(siteId),
        sessionsV2Api.getActiveSiteSessions(siteId).catch(() => [])
      ]);
      const executorsWithActiveSession = new Set<number>();
      activeSessions.forEach(s => {
        if (s.siteExecutorId) {
          executorsWithActiveSession.add(s.siteExecutorId);
        } else if (s.userId > 0) {
          const exec = executorsData.find(e => e.userId === s.userId);
          if (exec) executorsWithActiveSession.add(exec.id);
        }
      });
      const executorsWithoutOpenSession = executorsData.filter(e => !executorsWithActiveSession.has(e.id));
      setExecutors(executorsWithoutOpenSession);
    } catch (err) {
      console.error('Error loading executors:', err);
      setError('Ошибка загрузки исполнителей объекта');
    } finally {
      setExecutorsLoading(false);
    }
  };

  const handleSelectionChange = useCallback((selectedIds: string[]) => {
    setSelectedExecutorIds(selectedIds.map(id => parseInt(id, 10)));
  }, []);

  const handleSelectAll = () => {
    const allExecutorIds = executors.map(e => e.id);
    if (selectedExecutorIds.length === allExecutorIds.length) {
      setSelectedExecutorIds([]);
    } else {
      setSelectedExecutorIds(allExecutorIds);
    }
  };

  const handleConfirm = useCallback(async () => {
    if (selectedExecutorIds.length === 0) return;
    await onConfirm(selectedExecutorIds);
    setSelectedExecutorIds([]);
  }, [selectedExecutorIds, onConfirm]);

  const handleCancel = () => {
    setSelectedExecutorIds([]);
    onClose();
  };

  const userListUsers = useMemo(() => {
    const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
    const getAvatarColor = (id: number): string => {
      const colors = [
        '#ef4444', '#f97316', '#f59e0b', '#eab308',
        '#84cc16', '#22c55e', '#10b981', '#06b6d4',
        '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
        '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
      ];
      return colors[id % colors.length];
    };

    return executors.map(executor => {
      const displayName = executor.userName || executor.displayName || 'Неизвестный';
      const avatarUrl = executor.avatarUrl
        ? (executor.avatarUrl.startsWith('http') ? executor.avatarUrl : `${apiBaseUrl}${executor.avatarUrl}`)
        : undefined;

      return {
        userId: executor.id.toString(),
        userName: displayName + (executor.isExternal ? ' (внешний)' : ''),
        userRole: executor.isExternal ? 'Внешний исполнитель' : undefined,
        avatarUrl,
        avatarColor: getAvatarColor(executor.id)
      } as User;
    });
  }, [executors]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleCancel}
      title="Массовое открытие смен"
      size="large"
      showHandle={true}
      elevated={elevated}
    >
      <div className="bulk-open-sessions-sheet">
        {executorsLoading ? (
          <div className="bulk-open-sessions-loading">
            <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка исполнителей...</span>
          </div>
        ) : error ? (
          <div className="bulk-open-sessions-error">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        ) : (
          <>
            <div className="bulk-open-sessions-header">
              <div className="bulk-open-sessions-title">
                <i className="ri-user-line"></i>
                <span>Выберите исполнителей</span>
              </div>
              <button
                type="button"
                className="bulk-open-sessions-select-all-btn"
                onClick={handleSelectAll}
              >
                {selectedExecutorIds.length === userListUsers.length 
                  ? 'Снять все' 
                  : 'Выбрать всех'}
              </button>
            </div>

            <div className="bulk-open-sessions-list">
              <div className="bulk-open-sessions-list-wrapper">
                <UserList
                  users={userListUsers}
                  multiSelect={true}
                  selectedUserIds={selectedExecutorIds.map(id => id.toString())}
                  onSelectionChange={handleSelectionChange}
                  showRemoveButton={false}
                  canManagePermissions={false}
                  canManageMembers={false}
                  className="bulk-open-sessions-user-list"
                />
              </div>
            </div>

            {selectedExecutorIds.length > 0 && (
              <div className="bulk-open-sessions-summary">
                <i className="ri-information-line"></i>
                <span>Выбрано исполнителей: {selectedExecutorIds.length}</span>
              </div>
            )}

            <div className="bulk-open-sessions-actions">
              <StatusActionButton
                variant="secondary"
                onClick={handleCancel}
                disabled={loading}
                fullWidth
                margin="0 0 0.75rem 0"
              >
                Отмена
              </StatusActionButton>
              <StatusActionButton
                variant="primary"
                onClick={handleConfirm}
                disabled={loading || selectedExecutorIds.length === 0}
                loading={loading}
                icon="ri-check-line"
                fullWidth
              >
                Подтвердить ({selectedExecutorIds.length})
              </StatusActionButton>
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default BulkOpenSessionsSheet;

